import React, { useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import Page from "../components/Page";
import ReportExportWrapper from "../components/ReportExportWrapper";
import { IconFilter, IconSearch, IconX, IconCalendar } from "@tabler/icons-react";
import { useGenericReport } from "../controllers/reports.controller";

export default function GenericReportPage() {
  const { reportType } = useParams();

  const formattedTitle = reportType
    .split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

  // Default to today as requested
  const [dateRange, setDateRange] = useState("today");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [filterSoldOnly, setFilterSoldOnly] = useState(false);

  const { data, isLoading } = useGenericReport({
    reportType,
    dateFilter: dateRange,
    from: dateRange === "custom" ? fromDate : null,
    to: dateRange === "custom" ? toDate : null,
  });

  const columns = data?.columns || [];
  const rawTableData = data?.tableData || [];
  const rawSummaryData = data?.summaryData || {};

  // Filter table data by search query and optional sold-only toggle
  const filteredData = useMemo(() => {
    let list = Array.isArray(rawTableData) ? rawTableData : [];

    if (filterSoldOnly && reportType === "fb") {
      list = list.filter((r) => Number(r.total_quantity || 0) > 0);
    }

    if (!searchQuery.trim()) {
      return list;
    }

    const query = searchQuery.toLowerCase().trim();
    return list.filter((row) => {
      const category = String(row.category_name || row.category || "").toLowerCase();
      const itemName = String(row.item_name || row.title || row.name || "").toLowerCase();
      const anyField = Object.values(row).some((val) =>
        String(val || "").toLowerCase().includes(query)
      );
      return category.includes(query) || itemName.includes(query) || anyField;
    });
  }, [rawTableData, searchQuery, filterSoldOnly, reportType]);

  // Dynamic summary cards for search results
  const summaryData = useMemo(() => {
    if (reportType === "fb") {
      const totalSold = filteredData.reduce((acc, r) => acc + Number(r.total_quantity || 0), 0);
      const totalRevenue = filteredData.reduce((acc, r) => acc + Number(r.total_revenue || 0), 0);
      
      if (searchQuery.trim() || filterSoldOnly) {
        return {
          "Total Items Listed": filteredData.length,
          "Total Items Sold (Qty)": totalSold,
          "Total F&B Revenue": totalRevenue.toFixed(2),
        };
      }
      return {
        ...rawSummaryData,
        "Total Items Sold": totalSold,
        "Total F&B Revenue": totalRevenue.toFixed(2),
      };
    }
    return rawSummaryData;
  }, [rawSummaryData, filteredData, searchQuery, filterSoldOnly, reportType]);

  const getDateRangeLabel = () => {
    switch (dateRange) {
      case "today":
        return "Today";
      case "yesterday":
        return "Yesterday";
      case "last_7days":
        return "Last 7 Days";
      case "last_30days":
        return "Last 30 Days";
      case "this_month":
        return "This Month";
      case "this_year":
        return "This Year";
      case "all_time":
        return "All Time";
      case "custom":
        return fromDate && toDate ? `${fromDate} to ${toDate}` : "Custom Date";
      default:
        return "Today";
    }
  };

  return (
    <Page>
      <ReportExportWrapper
        title={`${formattedTitle} Report`}
        reportType={formattedTitle}
        dateRange={getDateRangeLabel()}
        filters={searchQuery ? `Search: "${searchQuery}"` : "Default"}
        columns={columns}
        tableData={filteredData}
        summaryData={summaryData}
      >
        {/* Top Controls Bar: Search & Date Filters */}
        <div className="flex flex-col md:flex-row gap-4 justify-between items-stretch md:items-center mb-6 print:hidden">
          {/* Search Bar for Category and Item Name */}
          <div className="relative flex-1 max-w-lg">
            <IconSearch
              size={18}
              className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
            />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search category or item name..."
              className="w-full pl-10 pr-10 py-2.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl text-sm font-medium focus:outline-none focus:ring-2 focus:ring-restro-green/40 focus:border-restro-green transition"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition"
                title="Clear search"
              >
                <IconX size={16} />
              </button>
            )}
          </div>

          {/* Date Filter & Sold Only Toggle */}
          <div className="flex flex-wrap gap-2 items-center">
            {reportType === "fb" && (
              <button
                type="button"
                onClick={() => setFilterSoldOnly(!filterSoldOnly)}
                className={`btn btn-sm rounded-xl font-bold transition ${
                  filterSoldOnly
                    ? "btn-primary text-white"
                    : "btn-ghost bg-gray-50 border border-gray-200 dark:bg-gray-800 dark:border-gray-700 text-gray-600 dark:text-gray-300"
                }`}
              >
                {filterSoldOnly ? "Showing Sold Only" : "Show Sold Only"}
              </button>
            )}

            {/* Date Range Selector */}
            <div className="flex gap-2 items-center bg-gray-50 border border-gray-200 dark:bg-gray-800 dark:border-gray-700 p-1.5 rounded-xl text-sm font-bold">
              <IconCalendar size={16} className="ml-2 text-gray-400" />
              <span className="text-gray-500">Date:</span>
              <select
                value={dateRange}
                onChange={(e) => setDateRange(e.target.value)}
                className="bg-transparent border-none outline-none text-restro-green font-black cursor-pointer pr-2"
              >
                <option value="today">Today</option>
                <option value="yesterday">Yesterday</option>
                <option value="last_7days">Last 7 Days</option>
                <option value="last_30days">Last 30 Days</option>
                <option value="this_month">This Month</option>
                <option value="this_year">This Year</option>
                <option value="all_time">All Time</option>
                <option value="custom">Custom Date</option>
              </select>
            </div>

            {/* Custom Date Pickers */}
            {dateRange === "custom" && (
              <div className="flex gap-2 items-center bg-gray-50 border border-gray-200 dark:bg-gray-800 dark:border-gray-700 p-1 rounded-xl">
                <input
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  className="bg-transparent text-xs font-semibold px-2 py-1 outline-none border-none text-gray-700 dark:text-gray-200"
                />
                <span className="text-xs text-gray-400 font-bold">to</span>
                <input
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  className="bg-transparent text-xs font-semibold px-2 py-1 outline-none border-none text-gray-700 dark:text-gray-200"
                />
              </div>
            )}
          </div>
        </div>

        {/* Summary Cards */}
        {isLoading ? (
          <div className="flex justify-center p-8">
            <span className="loading loading-spinner text-restro-green"></span>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              {Object.entries(summaryData).map(([key, value], idx) => (
                <div
                  key={idx}
                  className="p-6 bg-gray-50 dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700"
                >
                  <p className="text-xs font-black text-gray-500 uppercase tracking-widest mb-2">
                    {key}
                  </p>
                  <h3 className="text-3xl font-black">
                    {typeof value === 'object' && value !== null ? JSON.stringify(value) : String(value ?? "-")}
                  </h3>
                </div>
              ))}
            </div>

            {/* Search filter indicator */}
            {searchQuery && (
              <div className="mb-4 text-xs font-bold text-gray-500 flex items-center justify-between">
                <span>
                  Showing {filteredData.length} of {rawTableData.length} items matching &quot;{searchQuery}&quot;
                </span>
                <button
                  type="button"
                  onClick={() => setSearchQuery("")}
                  className="text-restro-green hover:underline cursor-pointer"
                >
                  Clear search
                </button>
              </div>
            )}

            {/* Standard Table View */}
            <div className="overflow-x-auto border border-gray-100 dark:border-gray-800 rounded-2xl">
              <table className="w-full text-left text-sm">
                <thead className="bg-gray-50 dark:bg-gray-800 border-b border-gray-100 dark:border-gray-700">
                  <tr>
                    {columns.map((c, i) => (
                      <th
                        key={i}
                        className="p-4 font-bold text-gray-500 uppercase tracking-wider text-xs"
                      >
                        {c.header}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                  {filteredData.length === 0 ? (
                    <tr>
                      <td
                        colSpan={columns.length || 1}
                        className="p-8 text-center text-gray-500"
                      >
                        {searchQuery
                          ? `No items found matching "${searchQuery}".`
                          : "No data available for this period."}
                      </td>
                    </tr>
                  ) : (
                    filteredData.map((row, i) => (
                      <tr
                        key={i}
                        className="hover:bg-gray-50/50 dark:hover:bg-gray-800/50 transition-colors duration-150"
                      >
                        {columns.map((c, j) => {
                          const val = row[c.key];
                          const isNumeric = c.key === "total_quantity" || c.key === "total_revenue";
                          return (
                            <td
                              key={j}
                              className={`p-4 ${
                                j === 0
                                  ? "font-bold text-gray-800 dark:text-gray-200"
                                  : isNumeric
                                  ? "font-semibold"
                                  : ""
                              }`}
                            >
                              {val !== undefined && val !== null ? (typeof val === 'object' ? JSON.stringify(val) : String(val)) : "-"}
                            </td>
                          );
                        })}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </ReportExportWrapper>
    </Page>
  );
}
